import { useEffect, useRef, useState, type ReactNode } from "react";

import { type TraySearch, TraySearchResults } from "@/components/kory/tray-search-results";
import { CaptionFold } from "@/components/overview/cos-morning/cos-live-turn";
import { AskChips, type TrayAsks } from "@/components/overview/cos-v2/talk-unit";
import type { TraySendSource } from "@/lib/kory-tray-events";
import { cn } from "@/lib/utils";

const SEARCH_BAND_EXIT_MS = 100;

/** One crossfade for the two layers in the shared band. Asks leave upward. Results leave downward. */
function BandLayer({
  visible,
  from,
  children,
}: {
  visible: boolean;
  from: "above" | "below";
  children: ReactNode;
}) {
  return (
    <div
      aria-hidden={visible ? undefined : true}
      inert={visible ? undefined : true}
      className={cn(
        "absolute inset-0 transition-[opacity,transform] motion-reduce:transition-none",
        visible
          ? "translate-y-0 opacity-100 duration-150 ease-[var(--eo)]"
          : cn("pointer-events-none opacity-0 duration-100 ease-in", from === "above" ? "-translate-y-0.5" : "translate-y-0.5"),
      )}
    >
      {children}
    </div>
  );
}

/** Keep the previous rows just long enough for the shared band's exit fade.
 *  The key ignores object identity: the search value is a new object every render. */
function searchBandKey(search: TraySearch | null): string {
  if (!search) return "";
  return `${search.query}\n${search.stale ? 1 : 0}\n${search.shown.map((hit) => hit.key).join("\n")}`;
}

export function useSearchBandRows(search: TraySearch | null): [TraySearch | null, () => void] {
  const [rows, setRows] = useState<TraySearch | null>(search);
  const key = searchBandKey(search);
  const latest = useRef(search);
  latest.current = search;
  useEffect(() => {
    const next = latest.current;
    if (next) {
      setRows(next);
      return;
    }
    const timer = window.setTimeout(() => setRows(null), SEARCH_BAND_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [key]);
  return [rows, () => setRows(null)];
}

/**
 * Pills and lookup rows occupy one band, under the composer and outside the chat card.
 * The resting asks return when a lookup becomes a sentence, so no empty band remains.
 * On a phone the band stays right under the composer and the to-dos scroll beneath it.
 * A numbers page's openers rest here in place of the asks. A live call speaks instead.
 * Working in the to-dos, the asks hold their place until lookup rows exist.
 * Resting pills size the band to their rows: one row for two pills side by side, two for a third.
 * Lookup rows always get the full band. The height eases like CaptionFold.
 */
export function SuggestionSearchBand({
  talking,
  search,
  rows,
  asks,
  compact,
  mobile,
  onSend,
  onOpenResult,
}: {
  talking: boolean;
  search: TraySearch | null;
  /** The rows the band shows, kept through their exit fade (`useSearchBandRows`). */
  rows: TraySearch | null;
  asks: TrayAsks | null;
  compact: boolean;
  mobile: boolean;
  onSend: (text: string, source: TraySendSource) => void;
  onOpenResult: (url: string) => void;
}) {
  if (talking || !(search || rows || asks?.asks.length)) return null;
  return (
    <CaptionFold open={!compact || Boolean(search)}>
      {/* Pill rows are h-11 below sm and h-8 at sm+, with gap-1.5, in rem so they track the root scale. */}
      <div
        data-testid="tray-suggestion-search-band"
        className={cn(
          "relative transition-[height] duration-200 ease-[var(--edr)] motion-reduce:transition-none",
          search || !asks?.asks.length
            ? "h-[5.875rem] sm:h-[88px] lg:h-20"
            : asks.asks.length > 2
              ? "h-[5.875rem] sm:h-[4.375rem]"
              : "h-11 sm:h-8",
        )}
      >
        {asks?.asks.length ? (
          <BandLayer visible={!search} from="above">
            <AskChips
              trayAsks={asks}
              onPick={(ask) => onSend(ask, asks.kind === "openers" ? "prompt_chip" : "ask_chip")}
              hidden={Boolean(search)}
            />
          </BandLayer>
        ) : null}
        {rows ? (
          <BandLayer visible={Boolean(search)} from="below">
            <TraySearchResults search={rows} mobile={mobile} onOpen={onOpenResult} />
          </BandLayer>
        ) : null}
      </div>
    </CaptionFold>
  );
}
